import { ChangeDetectionStrategy, Component, Input, OnInit, EventEmitter, Output, ChangeDetectorRef, signal, WritableSignal, ModelSignal, model } from '@angular/core';
import { BehaviorSubject, combineLatest, map, Observable, of, Subject, switchMap } from 'rxjs';
import { CommentService, WorkItemComment, WorkItemCommentHistory, CommentEdited } from 'src/app/api/comment.service';
import { UiSafeStaff } from 'src/app/api';
import { LocalAuthService } from 'src/app/auth/local-auth.service';
import { MessageService } from 'primeng/api';

@Component({
    selector: 'app-work-item-comment-view',
    templateUrl: './work-item-comment-view.component.html',
    styleUrls: ['./work-item-comment-view.component.scss'],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class WorkItemCommentViewComponent implements OnInit {

  private _workItemId!: number;
  private _workItemCommentId!: number;
  displayModal: ModelSignal<boolean> = model(false);
  commentText: ModelSignal<string> = model('');
  visible: WritableSignal<boolean> = signal(false);
  oldCommentText: WritableSignal<string> = signal('');
  editedComment: WritableSignal<string> = signal('');
  


  private newComment$ = new BehaviorSubject<WorkItemComment[]>([])
  private _newComments: WorkItemComment[] = [];

  private commentEdited$ = new BehaviorSubject<WorkItemCommentHistory[]>([])
  private _commentEdited: WorkItemCommentHistory[] = []

  @Output() CommentSubmitted = new EventEmitter<boolean>()
  @Input() set workItemId(id: number) {
    this._workItemId = id;
    if (id > 0) {
      this.comments$ = combineLatest([this.commentService.getComments(id), this.newComment$])
        .pipe(
          map(([comments, newComments]) => {
            const commentMap = new Map<number, WorkItemComment>();

            comments.forEach(comment => commentMap.set(comment.workItemCommentId, comment));

            newComments.forEach(newComment => commentMap.set(newComment.workItemCommentId, newComment));

            return Array.from(commentMap.values());
          })
        );
    } else {
      this.comments$ = of([]);
    }
  }

  get workItemId(): number {
    return this._workItemId;
  }

  @Input() allowNewComments: boolean = true;

  comments$: Observable<WorkItemComment[]> = of([]);

  constructor(
    private commentService: CommentService,
    private chRef: ChangeDetectorRef,
    private messageService: MessageService,
    private auth: LocalAuthService
  ) { }

  ngOnInit(): void {
    this.comments$.subscribe();
  }

  getCommentById(commentId: number) {
    this.comments$.subscribe(comments => {

      const comment = comments.find(c => c.workItemCommentId === commentId);

      if (comment) {
        this.commentText.set(comment.commentText);
        this.oldCommentText.set(comment.commentText);
        this._workItemCommentId = comment.workItemCommentId;
        this._workItemId = comment.workItemId;

      }
    });
  }

  editComment(commentId: number) {
    this.getCommentById(commentId);
    this.showDialog()
  }



  onCommentSaved(newComment: WorkItemComment) {
    this._newComments.push(newComment);
    this.newComment$.next([...this._newComments]);

  }

  onCommentEdited(editedComment: WorkItemComment) {

    const index = this._newComments.findIndex(comment => comment.workItemCommentId === editedComment.workItemCommentId);

    if (index !== -1) {
      this._newComments[index] = editedComment;
    } else {
      this._newComments.push(editedComment);
    }

    this.newComment$.next([...this._newComments]);

  }

  onCommentEditedSave() {
    
    this.commentService.saveCommentEdit(this._workItemId, this.commentText() ?? '', this._workItemCommentId).subscribe({
      next: (editedComment: WorkItemComment) => {
        
        this._commentEdited = this._commentEdited.filter(
          (comment) => comment.workItemCommentId !== editedComment.workItemCommentId
        );

        const commentHistory: WorkItemCommentHistory = {
          workItemCommentId: editedComment.workItemCommentId,
          workItemId: editedComment.workItemId,
          commentText: editedComment.commentText,
          staffId: editedComment.staffId ?? 0, 
          editedDay: new Date(),
          oldCommentText: '', 
          commentHistoryId: 0, 
          createdAt: new Date() 
        };
  
        this._commentEdited.push(commentHistory);
  
        this.commentEdited$.next([...this._commentEdited]);
        this.displayModal.set(false);
        
        this.onCommentEdited(editedComment);
        this.hideDialog();
        this.CommentSubmitted.emit(true);
        this.chRef.detectChanges();
        this.commentService.getComments(this.workItemId);
        this.messageService.add({ severity: "success", detail: "Comment Edited successfully." });
      },
      error: (error: any) => {
        this.messageService.add({ severity: "error", detail: "There was a problem editing the Comment" });
      }
    });
  }

  showDialog() {
    this.displayModal.set(true);
  }

  hideDialog() {
    this.displayModal.set(false);
  }

}
